import { resolveHttpError } from '@sakhtar/design-system/react';
import { ApiError } from './apiClient.js';

/** Per-form dictionary of server field `path` → Persian inline message. */
export type FieldMessages = Record<string, string>;

/**
 * Server `details[]` → Persian per-field messages, for forms that don't need
 * `crm/leads/leadUi.ts`'s per-form special-casing. Any `details[]` path not
 * present in `messages` is dropped — it still surfaces via `bannerMessageFrom`.
 */
export function fieldErrorsFrom(error: unknown, messages: FieldMessages): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const result: Record<string, string> = {};
  for (const detail of error.envelope.error.details ?? []) {
    const message = messages[detail.path];
    if (message) result[detail.path] = message;
  }
  return result;
}

/**
 * Persian banner text for an error that isn't (fully) mapped to a field.
 * Server messages are English (`apps/api/src/http/errors.ts` has no Persian
 * localisation), so this never surfaces `error.envelope.error.message`
 * directly — only the given Persian fallback or standard Persian status
 * messages. Any English string fallback is safely intercepted to prevent leaks.
 */
export function bannerMessageFrom(error: unknown, fallback?: string): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return 'اجازهٔ دسترسی به این بخش را ندارید.';
    if (error.status === 404) return 'اطلاعات مورد نظر یافت نشد.';
    if (error.status === 409) return 'این عملیات با وضعیت فعلی سازگار نیست.';
    if (error.status === 422) return 'اطلاعات ارسال‌شده معتبر نیست. لطفاً ورودی‌ها را بررسی کنید.';
    if (error.status === 500) return 'خطایی در سرور رخ داده است. لطفاً دوباره تلاش کنید.';
    if (error.status === 503)
      return 'سامانه در حال به‌روزرسانی است. لطفاً چند لحظه بعد دوباره تلاش کنید.';
    return resolveHttpError(error.status, fallback).description;
  }

  // If fallback is provided and contains Persian, use it; otherwise provide a standard safe message
  if (typeof fallback === 'string' && fallback.trim().length > 0) {
    const isEnglishOnly = /^[A-Za-z0-9\s.,!?:;'"_\-–—/\\()[\]{}]+$/.test(fallback.trim());
    if (!isEnglishOnly) {
      return fallback;
    }
  }
  return 'خطایی در انجام عملیات رخ داده است. لطفاً دوباره تلاش کنید.';
}
